import React, { useState, useEffect } from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import styled from 'styled-components';
import { Box, Text } from '../../components';
import { DealerRegistrationForm } from '../../components/DealerRegistrationForm';
import { dealerRegistrationService } from '../../services/dealerRegistration';
import logo from '../../images/logo.svg';

// Configuração do webhook N8N
const N8N_WEBHOOK_URL = 'https://n8n.instacarro.com/webhook/0d4260bd-33c8-4294-a04e-cdfa51171576';

const PageContainer = styled.div`
  width: 100%;
  min-height: calc(100vh - 59.19px);
  display: flex;
  flex-direction: column;
  background-color: #f5f5f5;

  @media (max-width: 768px) {
    overflow-y: scroll;
  }
`;

const ContentWrapper = styled.div`
  max-width: 100%;
  margin: 0 auto;
  width: 100%;
`;

const Header = styled.div`
  background-color: white;
  padding: 24px;
  text-align: center;
  border-bottom: 1px solid #e0e0e0;

  img {
    height: 40px;
  }

  @media (max-width: 768px) {
    padding: 16px;

    img {
      height: 32px;
    }
  }
`;

const FormCard = styled(Box)`
  background: white;
  margin: 40px auto;
  padding: 40px;
  max-width: 800px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;

  @media (max-width: 768px) {
    padding: 24px;
    margin: 20px;
  }
`;

const FormHeader = styled.div`
  margin-bottom: 32px;
  text-align: center;

  h1 {
    font-family: Roboto;
    color: #333;
    font-size: 24px;
    font-weight: 600;
    margin: 0 0 8px 0;
  }

  p {
    color: #666;
    font-family: Roboto;
    font-size: 14px;
    margin: 0;
    font-weight: 400;
  }

  @media (max-width: 768px) {
    h1 {
      font-size: 20px;
    }

    p {
      font-size: 13px;
    }
  }
`;

const InfoBanner = styled.div`
  background: #e3f2fd;
  border-left: 4px solid #2196f3;
  padding: 16px;
  border-radius: 4px;
  margin-bottom: 32px;

  p {
    margin: 0;
    color: #1565c0;
    font-size: 14px;
    line-height: 1.6;
  }

  strong {
    display: block;
    margin-bottom: 8px;
    font-size: 16px;
  }
`;

const Footer = styled.div`
  text-align: center;
  padding: 24px;
  color: #999;
  font-size: 12px;

  a {
    color: #666;
    text-decoration: none;
    margin: 0 8px;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  padding: 48px 40px;
  max-width: 500px;
  width: 90%;
  text-align: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);

  @media (max-width: 768px) {
    padding: 32px 24px;
  }
`;

const CheckIcon = styled.div`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  border: 2px solid #4caf50;
  margin: 0 auto 20px;
  display: flex;
  align-items: center;
  justify-content: center;

  &:after {
    content: '';
    display: block;
    width: 15px;
    height: 26px;
    border: solid #4caf50;
    border-width: 0 3px 3px 0;
    transform: rotate(45deg);
    margin-bottom: 6px;
  }
`;

const ModalTitle = styled.h2`
  font-family: Roboto;
  font-size: 24px;
  font-weight: 600;
  color: #4caf50;
  margin: 0 0 16px 0;
`;

const ModalDescription = styled.p`
  font-family: Roboto;
  font-size: 14px;
  color: #666;
  margin: 0 0 24px 0;
  line-height: 1.5;
`;

const PaymentBox = styled.div`
  background: #f9f9f9;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  padding: 20px;
  margin-bottom: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const PaymentLabel = styled.span`
  font-family: Roboto;
  font-size: 14px;
  font-weight: 600;
  color: #333;
`;

const PaymentValue = styled.span`
  font-family: Roboto;
  font-size: 16px;
  font-weight: 600;
  color: #333;
`;

const ModalButton = styled.button`
  background-color: #ff6b35;
  color: white;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color 0.3s;
  width: 100%;

  &:hover {
    background-color: #e65a2b;
  }
`;

/**
 * Função para extrair parâmetros da URL
 */
const useQuery = () => {
  return new URLSearchParams(useLocation().search);
};

/**
 * Página de cadastro de lojista (sem autenticação)
 * URL: /cadastrar-lojista?nome=NOME&email=EMAIL&customer_id=ID&subscription_value=VALOR
 */
export const DealerRegistrationPage = () => {
  const query = useQuery();
  const history = useHistory();
  const [status, setStatus] = useState({ success: false, error: false, message: '' });
  const [initialValues, setInitialValues] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [subscriptionValue, setSubscriptionValue] = useState('397,00');
  const [checkoutSessionId, setCheckoutSessionId] = useState(null);
  const [clientReferenceId, setClientReferenceId] = useState(null);
  const [sessionData, setSessionData] = useState({
    amount_total: null,
    customer: null,
    customer_details: null,
    success_url: null,
  });

  // Função para buscar dados da sessão do checkout
  const fetchCheckoutSession = async (sessionId) => {
    try {
      const response = await fetch(
        N8N_WEBHOOK_URL,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            checkout_session_id: sessionId,
          }),
        }
      );
      
      if (!response.ok) {
        throw new Error('Erro ao buscar dados da sessão');
      }

      const sessionData = await response.json();
      
      // Mapeia os dados da sessão para os campos do formulário
      const customerName = sessionData.customer_details?.name || '';
      const nameParts = customerName.trim().split(' ');
      const firstName = nameParts[0] || '';
      const lastName = nameParts.slice(1).join(' ') || '';

      // Converte amount_total de centavos para reais formatado
      const amountInReais = sessionData.amount_total ? (sessionData.amount_total / 100).toFixed(2).replace('.', ',') : '397,00';

      return {
        firstName,
        lastName,
        email: sessionData.customer_details?.email || '',
        phoneNumber: sessionData.customer_details?.phone || '',
        subscriptionValue: amountInReais,
        clientReferenceId: sessionData.client_reference_id || '',
        // Dados completos da sessão para enviar ao backend
        sessionInfo: {
          amount_total: sessionData.amount_total,
          customer: sessionData.customer,
          customer_details: sessionData.customer_details,
          success_url: sessionData.success_url,
        },
      };
    } catch (error) {
      console.error('Erro ao buscar dados da sessão:', error);
      return null;
    }
  };

  // Função para notificar n8n sobre a renderização da página
  const notifyPageRendered = async (sessionId) => {
    try {
      await fetch(N8N_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          event: 'page_rendered',
          checkout_session_id: sessionId,
          timestamp: new Date().toISOString(),
        }),
      });
    } catch (error) {
      console.error('Erro ao notificar n8n sobre renderização:', error);
    }
  };

  // Extrai os parâmetros da URL e pré-preenche o formulário
  useEffect(() => {
    const loadFormData = async () => {
      const urlParams = {
        nome: query.get('nome') || query.get('name') || '',
        sobrenome: query.get('sobrenome') || query.get('lastname') || '',
        email: query.get('email') || '',
        telefone: query.get('telefone') || query.get('phone') || '',
        subscription_value: query.get('subscription_value') || query.get('valor') || '',
        isModalActive: query.get('isModalActive') === 'true',
        checkout_session_id: query.get('checkout_session_id') || '',
        client_reference_id: query.get('client_reference_id') || '',
      };

      let formData = {};

      // Se houver checkout_session_id, busca os dados da sessão
      if (urlParams.checkout_session_id) {
        setCheckoutSessionId(urlParams.checkout_session_id);
        
        // Notifica n8n que a página foi renderizada
        await notifyPageRendered(urlParams.checkout_session_id);
        
        // Busca dados da sessão
        const checkoutData = await fetchCheckoutSession(urlParams.checkout_session_id);
        if (checkoutData) {
          formData = {
            firstName: checkoutData.firstName,
            lastName: checkoutData.lastName,
            email: checkoutData.email,
            phoneNumber: checkoutData.phoneNumber,
          };
          
          // Armazena client_reference_id vindo do n8n
          if (checkoutData.clientReferenceId) {
            setClientReferenceId(checkoutData.clientReferenceId);
          }
          
          // Salva dados da sessão para enviar ao backend
          if (checkoutData.sessionInfo) {
            setSessionData(checkoutData.sessionInfo);
          }
          
          // Define o valor da assinatura
          if (checkoutData.subscriptionValue) {
            setSubscriptionValue(checkoutData.subscriptionValue);
          }
        }
      }

      // Se não houver checkout_session_id ou se houver parâmetros diretos na URL, usa os parâmetros
      if (!urlParams.checkout_session_id || urlParams.nome || urlParams.email) {
        let firstName = urlParams.nome.trim();
        let lastName = urlParams.sobrenome.trim();

        if (!lastName && firstName.includes(' ')) {
          const nameParts = firstName.split(' ');
          firstName = nameParts[0] || '';
          lastName = nameParts.slice(1).join(' ') || '';
        }

        // Sobrescreve ou define dados do formulário
        if (firstName) formData.firstName = firstName;
        if (lastName) formData.lastName = lastName;
        if (urlParams.email) formData.email = urlParams.email;
        if (urlParams.telefone) formData.phoneNumber = urlParams.telefone;
      }

      // Verifica se deve abrir o modal
      if (urlParams.isModalActive) {
        setIsModalOpen(true);
      }

      // Define o valor da assinatura se fornecido
      if (urlParams.subscription_value) {
        setSubscriptionValue(urlParams.subscription_value);
      }

      setInitialValues(formData);

      // Log para debug (remover em produção)
      if (process.env.NODE_ENV === 'development') {
        console.log('Parâmetros da URL:', urlParams);
        console.log('Valores iniciais do formulário:', formData);
      }
    };

    loadFormData();
  }, []);

  // Handler para submissão do formulário
  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      setStatus({ success: false, error: false, message: '' });

      // Adiciona checkout_session_id e dados da sessão aos valores
      const formDataWithSession = {
        ...values,
        ...(checkoutSessionId && { checkout_session_id: checkoutSessionId }),
        ...(clientReferenceId && { client_reference_id: clientReferenceId }),
        ...(checkoutSessionId && sessionData.customer && {
          session_data: {
            amount_total: sessionData.amount_total,
            customer: sessionData.customer,
            customer_details: sessionData.customer_details,
            success_url: sessionData.success_url,
          },
        }),
      };

      // Log para debug - payload que será enviado
      console.log('📤 Payload enviado ao backend:', JSON.stringify(formDataWithSession, null, 2));

      // Chama o serviço para criar a empresa
      const response = await dealerRegistrationService.createCompany(formDataWithSession);

      console.log('✅ Cadastro criado com sucesso:', response);

      // Envia dados do cadastro para n8n
      try {
        await fetch('https://n8n.instacarro.com/webhook/ee8b8363-b600-4158-9a9f-166aa4b0a0e7', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            status: response.status || 'created',
            email: response.email || formData.email,
            mongo_id: response.mongo_id || response.id || response._id || '',
            deal_id: response.deal_id || response.hubspot_deal_id || '',
            company_crm_id: response.company_crm_id || '',
            cnpj: response.cnpj || response.cnpj_empresa || '',
            legal_name: response.legal_name || response.razao_social || '',
            success: response.success !== undefined ? response.success : true,
            message: response.message || '',
          }),
        });
        console.log('📨 Dados enviados para n8n webhook');
      } catch (webhookError) {
        console.error('⚠️ Erro ao enviar para n8n webhook:', webhookError);
        // Não bloqueia o fluxo se o webhook falhar
      }

      // Redireciona imediatamente para página de feedback
      window.location.href = 'https://venda.instacarro.com/lojistas-feedback';
    } catch (error) {
      console.error('Erro ao cadastrar lojista:', error);

      setStatus({
        success: false,
        error: true,
        message: error.message || 'Erro ao realizar cadastro. Por favor, tente novamente.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageContainer>
      <ContentWrapper>
        <Header>
          <img src={logo} alt="Instacarro" />
        </Header>

        <FormCard>
          <FormHeader>
            <h1>Complete seu cadastro</h1>
            <p>Informe os dados da sua empresa</p>
          </FormHeader>
          <DealerRegistrationForm
            initialValues={initialValues}
            onSubmit={handleSubmit}
            status={status}
          />
        </FormCard>

        <Footer>
          <a href="#">Termos de uso</a>
          <span>/</span>
          <a href="#">Política de Privacidade</a>
        </Footer>

        {/* Modal de Assinatura */}
        {isModalOpen && (
          <ModalOverlay onClick={() => setIsModalOpen(false)}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <CheckIcon />
              <ModalTitle>Agradecemos sua assinatura</ModalTitle>
              <ModalDescription>
                Um pagamento para InstaCarro aparecerá no seu extrato:
              </ModalDescription>
              <PaymentBox>
                <PaymentLabel>INSTACARRO</PaymentLabel>
                <PaymentValue>R$ {subscriptionValue}</PaymentValue>
              </PaymentBox>
              <ModalButton onClick={() => setIsModalOpen(false)}>
                Seguir para o último passo
              </ModalButton>
            </ModalContent>
          </ModalOverlay>
        )}
      </ContentWrapper>
    </PageContainer>
  );
};

export default DealerRegistrationPage;
